<template>
  <!-- First-visit hint over the canvas. Takes no pointer events: the press that
       dismisses it is also the press that starts the rotation it describes. -->
  <div class="first-visit-hint">
    <!-- The gesture sits in its own darker box on top of the overlay. -->
    <div class="first-visit-hint__gesture-box">
      <svg
        class="first-visit-hint__gesture"
        viewBox="-4 -8 48 48"
        fill="none"
        aria-hidden="true"
      >
        <!-- The fingertip's trail. Centred on the hand's pivot (24, 30), just
             outside the fingertip's reach, from where the tip starts (-136deg) to
             where it stops (-86deg). pathLength="1" lets the draw-in animate the
             dash as a fraction of the arc. -->
        <path
          class="first-visit-hint__trail"
          d="M1.70 8.46 A31 31 0 0 1 26.16 -0.92"
          pathLength="1"
        />
        <path
          class="first-visit-hint__arrow"
          d="M22.56 -4.39 L26.16 -0.92 L22.11 2.02"
        />
        <!-- The hand from hand-draw.svg, minus the curved trail that icon draws
             off the fingertip; the animated arc above takes its place. -->
        <g class="first-visit-hint__hand">
          <path
            d="M12.2159 10.4994L16.1159 21.2137C16.1494 21.2973 16.133 21.3644 16.0494 21.398C15.9823 21.4316 15.9323 21.398 15.8816 21.348L13.9066 19.2051C12.6173 17.8151 10.9766 17.7151 9.70444 18.7866C8.26444 20.008 8.2473 21.833 9.65444 23.558L15.2623 30.338C19.498 35.4609 24.4694 37.0509 30.028 35.0251C36.6744 32.6144 39.5037 26.5209 36.8244 19.1544L35.5694 15.723C34.2637 12.0901 31.7694 10.6001 28.8394 11.6216C28.0523 10.6001 26.8466 10.2487 25.5416 10.7173C25.0723 10.8967 24.6322 11.1449 24.2359 11.4537C23.3816 10.3487 22.0594 9.94657 20.6859 10.4323C20.3113 10.5757 19.9569 10.7671 19.6316 11.0016L17.723 5.77871C17.0037 3.78586 15.1952 2.94943 13.3373 3.61871C11.4623 4.30514 10.6252 6.09729 11.3444 8.08871L11.4787 8.45729ZM29.308 32.8323C24.9223 34.4394 20.8037 33.6187 16.9366 28.948L11.328 22.2016C10.7259 21.4987 10.7259 20.8123 11.2616 20.343C11.7637 19.8916 12.4502 20.0423 13.0023 20.6109L16.8694 24.6123C17.5223 25.2823 18.0916 25.3487 18.6444 25.148C19.2973 24.9137 19.5816 24.2109 19.3137 23.4909L13.3873 7.18514C13.1366 6.51514 13.4373 5.87943 14.0737 5.64443C14.7266 5.41014 15.3294 5.72871 15.5809 6.398L19.8166 18.0337C20.0166 18.5859 20.6366 18.8366 21.1887 18.6359C21.7244 18.4351 22.0087 17.8487 21.808 17.313L20.2844 13.1116C20.5194 12.8937 20.8373 12.6759 21.1559 12.5587C21.9416 12.2744 22.5952 12.6259 22.8966 13.4459L24.2359 17.1123C24.4359 17.6816 25.0559 17.8994 25.5916 17.698C26.1109 17.5137 26.4452 16.9623 26.2273 16.3759L25.1394 13.4123C25.3872 13.1644 25.6837 12.9705 26.0102 12.843C26.7973 12.5587 27.4502 12.9101 27.7516 13.7309L28.6387 16.1751C28.8559 16.7609 29.4759 16.9787 30.0116 16.7773C30.1405 16.7327 30.2593 16.6631 30.3611 16.5723C30.463 16.4816 30.5459 16.3716 30.605 16.2487C30.6641 16.1258 30.6982 15.9924 30.7055 15.8561C30.7128 15.7199 30.693 15.5837 30.6473 15.4551L29.978 13.6466C31.3837 13.1444 32.7066 14.283 33.6102 16.7944L34.6652 19.6573C36.9587 25.9851 34.8659 30.8066 29.308 32.8323Z"
            fill="white"
          />
        </g>
      </svg>
    </div>
    <p class="first-visit-hint__text">
      {{ $t("navigation.firstVisitHint") }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
// One loop: the hand turns about its palm while the trail draws in behind the
// fingertip, the arrowhead lands, then both fade and the hand swings back.
$cycle: 2.8s;

.first-visit-hint {
  // Covers the whole viewer column, above everything in it -- labels,
  // toolbars and the sidebar toggle (9999) -- but under the modals a user
  // opens themselves (dialogs at 60001, the share backdrop at 100000).
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: #0000005e;
  color: white;
  pointer-events: none;
}

.first-visit-hint__gesture-box {
  padding: 20px;
  border-radius: 12px;
  background: #00000040;
  line-height: 0;
}

.first-visit-hint__gesture {
  display: block;
  width: 76px;
  height: 76px;
  overflow: visible;
}

.first-visit-hint__trail,
.first-visit-hint__arrow {
  stroke: white;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.first-visit-hint__trail {
  stroke-dasharray: 1 1;
  animation: first-visit-trail $cycle ease-in-out infinite;
}

.first-visit-hint__arrow {
  animation: first-visit-arrow $cycle ease-in-out infinite;
}

.first-visit-hint__hand {
  transform-box: view-box;
  transform-origin: 24px 30px;
  animation: first-visit-hand $cycle ease-in-out infinite;
}

.first-visit-hint__text {
  margin: 0;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
}

@keyframes first-visit-hand {
  0%,
  12% {
    transform: rotate(-25deg);
  }
  62%,
  80% {
    transform: rotate(25deg);
  }
  100% {
    transform: rotate(-25deg);
  }
}

// Starts just short of fully hidden, so the round cap shows the fingertip's
// first touch as a dot.
@keyframes first-visit-trail {
  0%,
  12% {
    stroke-dashoffset: 0.98;
    opacity: 1;
  }
  62%,
  80% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  90%,
  100% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
}

@keyframes first-visit-arrow {
  0%,
  56% {
    opacity: 0;
  }
  64%,
  80% {
    opacity: 1;
  }
  90%,
  100% {
    opacity: 0;
  }
}

// Motion-sensitive users get the finished gesture as a still.
@media (prefers-reduced-motion: reduce) {
  .first-visit-hint__trail,
  .first-visit-hint__arrow,
  .first-visit-hint__hand {
    animation: none;
  }
  .first-visit-hint__trail {
    stroke-dashoffset: 0;
  }
  .first-visit-hint__hand {
    transform: rotate(25deg);
  }
}
</style>
